import React, { FunctionComponent } from 'react'
import { MessageDescriptor, useIntl } from 'react-intl'
import { ImageStyle } from 'react-native'

import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { getFormattedMessageSafe } from '@lyrahealth-inc/shared-app-logic/src/features/intl/utils/getFormattedMessageSafe'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { HalfMoonDivider } from '../../atoms/halfMoonDivider/HalfMoonDivider'
import { ChevronV2Icon } from '../../atoms/icons/ChevronV2Icon'
import { Image } from '../../atoms/image/Image'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Title } from '../../atoms/title/Title'
import { IS_WEB } from '../../constants'
import { BodyTextSize, CARD_TITLE_LINE_HEIGHT, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface CompactImageCardProps {
  title?: MessageDescriptor | string
  titleTextComponent?: React.ReactNode
  id?: string
  containerTestId?: string
  imageUrl?: string
  imageElement?: React.ReactElement
  onCardPress?: () => void
  formattedMessageValues?: Record<string, any>
  // Optional for one-off use cases where the default card styles don't apply. Should be used if it doesn't make sense to add to the enum above
  customStyle?: {
    cardContainerStyle?: CSSObject
    imageContainerStyle?: CSSObject
    contentContainerStyle?: CSSObject
    imageStyle?: ImageStyle
    titleContainerStyle?: CSSObject
  }
  shouldShowChevron?: boolean
  accessibilityLabel?: string
  isImageBottomCurved?: boolean
  hasShadow?: boolean
  horizontalLayout?: boolean
  titleSize?: TitleSize
}

const cardContainerStyles = (
  theme: ThemeType,
  customCardContainerStyle?: CSSObject,
  horizontalLayout?: boolean,
  hasShadow?: boolean,
) => {
  const {
    colors,
    componentStyles: { compactImageCard },
  } = theme
  return {
    width: '100%',
    borderRadius: compactImageCard.borderRadius,
    border: compactImageCard.showBorder ? `1px solid ${colors.borderDefault}` : 'none',
    overflow: 'hidden',
    ...(hasShadow && {
      shadowColor: colors.shadowLow,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.8,
      shadowRadius: 10,
      elevation: 5,
    }),
    ...(horizontalLayout && {
      flexDirection: 'row' as const,
      alignItems: 'center',
      backgroundColor: colors.cardBackgroundPrimary,
    }),
    ...customCardContainerStyle,
  }
}

const PressableCardContainer = styled(PressableOpacity)<{
  theme: ThemeType
  customCardContainerStyle?: CSSObject
  horizontalLayout?: boolean
  hasShadow?: boolean
}>(({ theme, customCardContainerStyle, horizontalLayout, hasShadow }) =>
  cardContainerStyles(theme, customCardContainerStyle, horizontalLayout, hasShadow),
)

const NonPressableCardContainer = styled.View<{
  theme: ThemeType
  customCardContainerStyle?: CSSObject
  horizontalLayout?: boolean
  hasShadow?: boolean
}>(({ theme, customCardContainerStyle, horizontalLayout, hasShadow }) =>
  cardContainerStyles(theme, customCardContainerStyle, horizontalLayout, hasShadow),
)

const ImageContainer = styled.View<{ customImageContainerStyle?: CSSObject }>(({ customImageContainerStyle }) => {
  return {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    ...customImageContainerStyle,
  }
})

const StyledImage = styled(Image)({
  width: '100%',
  height: '100%',
})

const TitleContainer = styled.View<{ customTitleContainerStyle?: CSSObject }>(({ customTitleContainerStyle }) => ({
  ...customTitleContainerStyle,
}))

const StyledTitle = styled(Title)(({ size }) => ({
  ...(size === TitleSize.XSMALL && { lineHeight: CARD_TITLE_LINE_HEIGHT }),
}))

const ContentContainer = styled.View<{
  customContentContainerStyle?: CSSObject
  theme: ThemeType
  horizontalLayout?: boolean
}>(({ customContentContainerStyle, theme, horizontalLayout }) => {
  return {
    ...(!horizontalLayout && { backgroundColor: theme.colors.cardBackgroundPrimary }),
    flexGrow: 1,
    alignItems: 'center',
    padding: theme.spacing['16px'],
    flexDirection: 'row',
    justifyContent: 'space-between',
    ...customContentContainerStyle,
  }
})

export const CompactImageCard: FunctionComponent<CompactImageCardProps> = ({
  title,
  titleTextComponent,
  id,
  containerTestId,
  imageUrl,
  imageElement,
  onCardPress,
  formattedMessageValues,
  customStyle,
  shouldShowChevron,
  accessibilityLabel,
  isImageBottomCurved,
  hasShadow,
  horizontalLayout,
  titleSize,
}) => {
  const theme = useTheme() as ThemeType
  const {
    colors,
    breakpoints: { isMobileSized },
    componentStyles: { compactImageCard },
  } = theme
  const { formatMessage } = useIntl()

  const { cardContainerStyle, imageContainerStyle, contentContainerStyle, imageStyle, titleContainerStyle } =
    customStyle || {}
  const formattedTitle = getFormattedMessageSafe(title, formatMessage, formattedMessageValues)
  const formattedTestId = `CompactImageCard${id ? `-${id}` : ''}`

  const cardContent = (
    <>
      <ImageContainer customImageContainerStyle={imageContainerStyle}>
        {imageUrl ? (
          <StyledImage
            source={imageUrl}
            testID={tID(`${formattedTestId}-image`)}
            style={{ ...imageStyle, ...(IS_WEB && { objectFit: 'cover' }) }}
            contentFit='cover'
          />
        ) : (
          imageElement
        )}
        {isImageBottomCurved && <HalfMoonDivider color={colors.cardBackgroundPrimary} useSimplifiedCurve />}
      </ImageContainer>

      <ContentContainer customContentContainerStyle={contentContainerStyle} horizontalLayout={horizontalLayout}>
        <TitleContainer customTitleContainerStyle={titleContainerStyle}>
          {titleTextComponent ? (
            titleTextComponent
          ) : compactImageCard.useBodyText ? (
            <BodyText numberOfLines={1} text={formattedTitle} color={colors.textSecondary} size={BodyTextSize.LARGE} />
          ) : (
            <StyledTitle
              text={formattedTitle}
              size={titleSize ?? (isMobileSized ? TitleSize.SMALL : TitleSize.MEDIUM)}
              color={colors.textPrimary}
              testID={tID(`${formattedTestId}-title`)}
            />
          )}
        </TitleContainer>
        {(shouldShowChevron || compactImageCard.shouldShowChevron) && <ChevronV2Icon fillColor={colors.iconDefault} />}
      </ContentContainer>
    </>
  )

  const sharedProps = {
    testID: tID(containerTestId ? containerTestId : formattedTestId),
    customCardContainerStyle: cardContainerStyle,

    horizontalLayout,
    hasShadow,
    theme,
  }

  if (onCardPress) {
    return (
      <PressableCardContainer
        {...sharedProps}
        onPress={onCardPress}
        accessibilityLabel={accessibilityLabel || formattedTitle}
      >
        {cardContent}
      </PressableCardContainer>
    )
  }

  return <NonPressableCardContainer {...sharedProps}>{cardContent}</NonPressableCardContainer>
}
